<template>
  <div class="one-click-upsell py-4">
    <div class="row my-4">
      <div class="col-12 col-md-5">
        <h3>{{ $t('PRODUCTS.TITLE.ADD_UPSELL_PRODUCT') }}</h3>
        <div class="smaller">
          {{ $t('PRODUCTS.TEXT.UPSELL_PRODUCT_DESCRIPTION') }}
        </div>
      </div>
      <div class="d-none d-md-block col-md-2"></div>
      <div class="col-12 col-md-5">
        <div class="form-group">
          <label>
            {{ $t('PRODUCTS.LABELS.PRODUCT_TO_UPSELL') }}
          </label>
          <v-select
            :options="allUpsellableTeacherProducts"
            label="product_name"
            code="id"
            v-model="productForm.upsell_product"
            :reduce="c => c.id"
          />
        </div>
      </div>
    </div>
    <hr />
    <div class="row my-4">
      <div class="col-12 col-md-5">
        <h3>{{ $t('PRODUCTS.TITLE.UPSELL_PRICING') }}</h3>
        <div class="smaller">
          {{ $t('PRODUCTS.TEXT.UPSELL_PRICING') }}
        </div>
      </div>
      <div class="d-none d-md-block col-md-2"></div>
      <div class="col-12 col-md-5">
        <div class="form-group">
          <label>
            {{ $t('PRODUCTS.LABELS.UPSELL_PRODUCT_PRICE') }}
          </label>
          <HMTextInput
            type="number"
            class="m-0 w-100"
            v-model="productForm.upsell_price"
            :rules="{ required: !!productForm.upsell_product, min_value: 0 }"
            :name="$t('PRODUCTS.LABELS.UPSELL_PRODUCT_PRICE')"
          >
            <template slot="prepend">
              {{ getCurrencySymbol || 'USD' }}
            </template>
            <template slot="append" v-if="selectedUpsellProductIsRecurring">
              {{ selectedRecurringTypeLabel }}
            </template>
          </HMTextInput>
          <div v-if="selectedUpsellProductIsMultipleRecurring" class="mt-3">
            <label>
              {{ $t('PRODUCTS.LABELS.UPSELL_RECURRING_OPTION') }}
            </label>
            <v-select
              :options="recurringTypes"
              v-model="productForm.upsell_recurring_type"
              label="label"
              code="code"
              :clearable="false"
              :reduce="t => t.code"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  :required="selectedUpsellProductIsMultipleRecurring"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
          </div>
        </div>
      </div>
    </div>
    <hr />
    <div class="row my-4">
      <div class="col-12 col-md-5">
        <h3>{{ $t('PRODUCTS.TITLE.COUNTDOWN_TIMER') }}</h3>
        <div class="smaller">
          {{ $t('PRODUCTS.TEXT.COUNTDOWN_TIMER') }}
        </div>
      </div>
      <div class="d-none d-md-block col-md-2"></div>
      <div class="col-12 col-md-5">
        <div class="form-group">
          <label>
            {{ $t('PRODUCTS.LABELS.COUNTDOWN_DURATION') }}
          </label>
          <v-select
            :options="countdownOptions"
            v-model="productForm.upsell_countdown"
            :clearable="false"
            label="label"
            code="code"
            :reduce="c => c.code"
          ></v-select>
        </div>
      </div>
    </div>
    <hr />
    <div class="row my-4">
      <div class="col-12 col-md-5">
        <h3>{{ $t('PRODUCTS.TITLE.UPSELL_CUSTOM_HEADING') }}</h3>
        <div class="smaller">
          {{ $t('PRODUCTS.TEXT.UPSELL_CUSTOM_HEADING') }}
        </div>
      </div>
      <div class="d-none d-md-block col-md-2"></div>
      <div class="col-12 col-md-5">
        <div class="form-group">
          <label>
            {{ $t('PRODUCTS.LABELS.UPSELL_MESSAGE') }}
          </label>
          <HMTextInput
            type="text"
            class="m-0 w-100"
            v-model="productForm.upsell_title"
            :name="$t('PRODUCTS.LABELS.UPSELL_MESSAGE')"
            placeholder="Limited time offer!"
          >
          </HMTextInput>
        </div>
      </div>
    </div>
    <hr />
    <div class="row my-4">
      <div class="col-12 col-md-5">
        <h3>{{ $t('PRODUCTS.TITLE.UPSELL_DESCRIPTION') }}</h3>
        <div class="smaller">
          {{ $t('PRODUCTS.TEXT.UPSELL_DESCRIPTION') }}
        </div>
      </div>
      <div class="d-none d-md-block col-md-2"></div>
      <div class="col-12 col-md-5">
        <div class="form-group">
          <label>
            {{ $t('PRODUCTS.LABELS.UPSELL_DESCRIPTION') }}
          </label>
          <HMTextInput
            type="text"
            class="m-0 w-100"
            v-model="productForm.upsell_description"
            :name="$t('PRODUCTS.LABELS.UPSELL_DESCRIPTION')"
          >
          </HMTextInput>
        </div>
      </div>
    </div>
    <hr />
    <div class="row my-4">
      <div class="col-12 col-md-5">
        <h3>{{ $t('PRODUCTS.TITLE.CUSTOM_THUMBNAIL_OR_VIDEO') }}</h3>
        <div class="smaller">
          {{ $t('PRODUCTS.TEXT.CUSTOM_THUMBNAIL_OR_VIDEO') }}
        </div>
      </div>
      <div class="d-none d-md-block col-md-2"></div>
      <div class="col-12 col-md-5">
        <div class="w-100 text-right">
          <HMSwitch v-model="productForm.upsell_use_media"></HMSwitch>
        </div>
        <div v-if="productForm.upsell_use_media">
          <div v-if="!productForm.upsell_media" class="w-100 text-right">
            <HMButton color="primary" @click="showAddMediaForm">
              <font-awesome-icon :icon="['far', 'file-upload']" />
              <span class="text-nowrap ml-2">
                {{ $t('PRODUCTS.BUTTON.ADD_MEDIA') }}
              </span>
            </HMButton>
          </div>
          <div v-else class="mt-3">
            <HMFilePreview
              :file="productForm.upsell_media.media"
            ></HMFilePreview>
            <div class="w-100 text-right mt-2">
              <HMButton
                color="primary"
                @click="productForm.upsell_media = null"
              >
                <font-awesome-icon :icon="['far', 'trash']" />
                <span class="text-nowrap ml-2">
                  {{ $t('PRODUCTS.BUTTON.REMOVE_MEDIA') }}
                </span>
              </HMButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import HMTextInput from '@/web/components/common/HMTextInput';
import MediaFormModal from '@/web/components/media-library/MediaFormModal';
import LibraryAddMedia from '@/web/views/admin/media-library/LibraryAddMedia';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';
import HMFilePreview from '@/web/components/common/HMFilePreview';

export default Vue.extend({
  name: 'ProductOneClickUpsell',
  components: { HMFilePreview, HMTextInput },
  mixins: [TeacherPermissionsMixin],
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    ...mapGetters({
      productForm: 'product/productForm',
      allUpsellableTeacherProducts: 'product/allUpsellableTeacherProducts',
    }),
    productId() {
      return parseInt(this.$route.params['id']);
    },
    getCurrencySymbol() {
      return this.productForm.currency;
    },
    countdownOptions() {
      return [
        {
          label: 'Disable upsell',
          code: 0,
        },
        {
          label: '1 minute',
          code: 1,
        },
        {
          label: '3 minutes',
          code: 3,
        },
        {
          label: '5 minutes',
          code: 5,
        },
        {
          label: '10 minutes',
          code: 10,
        },
        {
          label: '15 minutes',
          code: 15,
        },
        {
          label: '30 minutes',
          code: 30,
        },
        {
          label: '60 minutes',
          code: 60,
        },
      ];
    },
    selectedUpsellProduct() {
      return this.allUpsellableTeacherProducts.find(
        p => p.id === this.productForm.upsell_product
      );
    },
    selectedUpsellProductIsRecurring() {
      const product = this.selectedUpsellProduct;
      if (!product) return false;
      return product.pricing_type === 'recurring';
    },
    selectedUpsellProductIsMultipleRecurring() {
      const product = this.selectedUpsellProduct;
      if (!product) return false;
      return (
        product.pricing_type === 'recurring' &&
        product.recurring_type === 'multiple'
      );
    },
    selectedUpsellProductRecurringType() {
      return this.selectedUpsellProduct?.recurring_type;
    },
    assignedUpsellProductRecurringType() {
      return (
        this.productForm?.upsell_recurring_type?.label ||
        this.productForm?.upsell_recurring_type
      );
    },
    recurringTypes() {
      return [
        {
          code: 'annually',
          label: this.$t('GENERAL.RECURRING_TYPE.ANNUALLY'),
        },
        {
          code: 'biannually',
          label: this.$t('GENERAL.RECURRING_TYPE.BIANNUALLY'),
        },
        {
          code: 'quarterly',
          label: this.$t('GENERAL.RECURRING_TYPE.QUARTERLY'),
        },
        {
          code: 'monthly',
          label: this.$t('GENERAL.RECURRING_TYPE.MONTHLY'),
        },
        {
          code: 'weekly',
          label: this.$t('GENERAL.RECURRING_TYPE.WEEKLY'),
        },
        {
          code: 'daily',
          label: this.$t('GENERAL.RECURRING_TYPE.DAILY'),
        },
      ];
    },
    selectedRecurringTypeLabel() {
      return this.recurringTypes.find(
        t => t.code === this.productForm.upsell_recurring_type
      )?.label;
    },
  },
  methods: {
    async showAddMediaForm() {
      this.$modal.show(
        MediaFormModal,
        {
          showSelectMedia: true,
          multiSelect: false,
          showEditMedia: false,
          videoUploadAllowed: this.videoUploadAllowed,
        },
        { classes: ['vm--inline-modal'], clickToClose: false },
        {
          'media-selected': async content => {
            console.log('selected media', content);
            this.productForm.upsell_media = content[0];
          },
          'media-added': media => {
            this.showEditMedia(media);
          },
        }
      );
    },
    showEditMedia(media) {
      this.$modal.show(
        LibraryAddMedia,
        {
          medaiData: media,
          showAsModal: true,
        },
        { classes: ['vm--inline-modal'] },
        {
          'media-saved': async media => {
            console.log('media saved', media);
            this.productForm.upsell_media = media;
          },
        }
      );
    },
  },
  mounted() {
    this.$store.dispatch('product/getTeacherProducts');
  },
  watch: {
    selectedUpsellProduct: {
      handler(newProduct, oldProduct) {
        if (!newProduct) {
          this.productForm.upsell_recurring_type = null;
        } else if (newProduct && newProduct !== oldProduct) {
          if (this.productForm.upsell_recurring_type) {
            return;
          }
          if (!this.selectedUpsellProductIsMultipleRecurring) {
            this.productForm.upsell_recurring_type = this.selectedUpsellProductRecurringType;
          } else {
            this.productForm.upsell_recurring_type = this.recurringTypes[0];
          }
        }
      },
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
</style>
